// Edit Role Page - Roles & Permissions Management
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, Loader2, Clock, FileText, CheckSquare, Settings, BarChart3, User, Users, ArrowRight, Mail, LayoutDashboard, ClipboardList, Sparkles, ClipboardCheck, FileBarChart, Plug, Building2, AlertCircle, Settings2, ChevronDown, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useGlobalSettings } from '@/hooks/use-global-settings';
import { useDataSource } from '@/contexts/DataSourceContext';
import { getUserRoleService, type UserRole, type UserRolePermissions, type CreateUserRoleDto, type UpdateUserRoleDto, type UserRoleListItem } from '@/services/userRoleService';
import { DEFAULT_ROLE_PERMISSIONS_MAP } from '@/services/defaultRolePermissions';

interface EditRolePageProps {
  onNavigate?: (path: string) => void;
}

interface Permission {
  id: string;
  name: string;
  description: string;
}

interface PermissionGroup {
  name: string;
  permissions: Permission[];
}

interface PermissionCategory {
  id: string;
  name: string;
  description: string;
  iconName: 'LayoutDashboard' | 'Clock' | 'FileText' | 'CheckSquare' | 'BarChart3' | 'Settings';
  iconColor: string;
  scope: 'basic' | 'management' | 'admin' | 'advanced'; // For color coding
  permissions: Permission[];
  groups?: PermissionGroup[]; // Optional grouping within category
}

// Helper to render icons
const getIcon = (iconName: string, className?: string) => {
  const iconProps = { className: className || "h-5 w-5" };
  switch (iconName) {
    case 'LayoutDashboard': return <LayoutDashboard {...iconProps} />;
    case 'Clock': return <Clock {...iconProps} />;
    case 'FileText': return <FileText {...iconProps} />;
    case 'CheckSquare': return <CheckSquare {...iconProps} />;
    case 'BarChart3': return <BarChart3 {...iconProps} />;
    case 'Settings': return <Settings {...iconProps} />;
    default: return null;
  }
};

// Permission categories aligned with PERMISSIONS_LIST.md (62 total permissions in 9 categories)
const permissionCategories: PermissionCategory[] = [
  {
    id: 'dashboard',
    name: 'Dashboard Permissions',
    description: 'Access to dashboard views and metrics',
    iconName: 'LayoutDashboard',
    iconColor: '', // Will use scope color
    scope: 'basic',
    permissions: [
      { id: 'dashboard.view_own', name: 'View Own Dashboard', description: 'View personal dashboard with own metrics and action items' },
      { id: 'dashboard.view_team', name: 'View Team Dashboard', description: 'View team dashboard with team member metrics' },
      { id: 'dashboard.view_department', name: 'View Department Dashboard', description: 'View department-wide dashboard with aggregated metrics' },
    ],
  },
  {
    id: 'timesheet',
    name: 'Timesheet Permissions',
    description: 'Manage timesheet entry and tracking capabilities',
    iconName: 'Clock',
    iconColor: '', // Will use scope color
    scope: 'basic',
    groups: [
      {
        name: 'View Access',
        permissions: [
          { id: 'timesheet.view_own', name: 'View Own Timesheets', description: 'View and access personal timesheet entries' },
          { id: 'timesheet.view_team', name: 'View Team Timesheets', description: 'View timesheet entries for direct reports' },
          { id: 'timesheet.view_all', name: 'View All Timesheets', description: 'View any employee\'s timesheet entries' },
        ],
      },
      {
        name: 'Entry Management',
        permissions: [
          { id: 'timesheet.create', name: 'Create Time Entries', description: 'Add new time entries to timesheets' },
          { id: 'timesheet.edit_own', name: 'Edit Own Entries', description: 'Modify personal time entries (status-dependent)' },
          { id: 'timesheet.edit_team', name: 'Edit Team Entries', description: 'Modify team member time entries (limited)' },
          { id: 'timesheet.edit_all', name: 'Edit All Entries', description: 'Modify any timesheet entry (admin override)' },
          { id: 'timesheet.delete_own', name: 'Delete Own Entries', description: 'Remove personal time entries' },
          { id: 'timesheet.delete_team', name: 'Delete Team Entries', description: 'Remove team member time entries' },
        ],
      },
      {
        name: 'Workflow Actions',
        permissions: [
          { id: 'timesheet.submit', name: 'Submit Timesheet', description: 'Submit timesheet for approval' },
          { id: 'timesheet.recall', name: 'Recall Timesheet', description: 'Recall a submitted timesheet' },
          { id: 'timesheet.copy', name: 'Copy Timesheet', description: 'Copy entries from previous or selected weeks' },
          { id: 'timesheet.view_audit', name: 'View Audit History', description: 'View timesheet change history and audit trail' },
        ],
      },
    ],
    permissions: [], // Permissions are in groups
  },
  {
    id: 'approvals',
    name: 'Approval Permissions',
    description: 'Manage timesheet approval workflows',
    iconName: 'CheckSquare',
    iconColor: '', // Will use scope color
    scope: 'management',
    permissions: [
      { id: 'approvals.view_queue', name: 'View Approval Queue', description: 'Access the approval dashboard and pending queue' },
      { id: 'approvals.approve', name: 'Approve Timesheets', description: 'Approve submitted timesheet entries' },
      { id: 'approvals.reject', name: 'Reject Timesheets', description: 'Reject timesheets with reason' },
      { id: 'approvals.bulk_approve', name: 'Bulk Approve', description: 'Approve multiple timesheets at once' },
      { id: 'approvals.view_details', name: 'View Approval Details', description: 'View detailed timesheet information during approval' },
      { id: 'approvals.view_automation', name: 'View Automation Suggestions', description: 'View AI-powered automation suggestions' },
      { id: 'approvals.manage_automation', name: 'Manage Automation Rules', description: 'Configure and manage approval automation rules' },
    ],
  },
  {
    id: 'reports',
    name: 'Reports & Analytics Permissions',
    description: 'Access reporting and analytics features',
    iconName: 'BarChart3',
    iconColor: '', // Will use scope color
    scope: 'management',
    groups: [
      {
        name: 'Standard Reports',
        permissions: [
          { id: 'reports.view_own', name: 'View Own Reports', description: 'View personal timesheet and utilization reports' },
          { id: 'reports.view_team', name: 'View Team Reports', description: 'View reports for direct reports' },
          { id: 'reports.view_department', name: 'View Department Reports', description: 'View reports for entire department' },
          { id: 'reports.view_all', name: 'View All Reports', description: 'View organization-wide reports' },
          { id: 'reports.export', name: 'Export Reports', description: 'Export report data to CSV, Excel, or PDF' },
        ],
      },
    ],
    permissions: [], // Permissions are in groups
  },
  {
    id: 'team',
    name: 'Team Management Permissions',
    description: 'Manage team members and their details',
    iconName: 'Settings',
    iconColor: '', // Will use scope color
    scope: 'management',
    permissions: [
      { id: 'team.view_directory', name: 'View Employee Directory', description: 'View the employee directory and basic information' },
      { id: 'team.view_details', name: 'View Employee Details', description: 'View detailed employee information and profiles' },

    ],
  },
  {
    id: 'projects',
    name: 'Project Management Permissions',
    description: 'Control project and task management access',
    iconName: 'FileText',
    iconColor: '', // Will use scope color
    scope: 'management',
    permissions: [
      { id: 'projects.view_assigned', name: 'View Assigned Projects', description: 'View only projects assigned to the user' },
      { id: 'projects.view_all', name: 'View All Projects', description: 'View all active and archived projects' },
      { id: 'projects.create', name: 'Create Projects', description: 'Create new projects' },
      { id: 'projects.edit', name: 'Edit Projects', description: 'Modify project details and settings' },
      { id: 'projects.delete', name: 'Delete Projects', description: 'Delete projects (with restrictions)' },
      { id: 'projects.archive', name: 'Archive Projects', description: 'Archive completed or inactive projects' },
      { id: 'projects.assign', name: 'Assign Projects', description: 'Assign projects to team members' },
      { id: 'projects.manage_budget', name: 'Manage Project Budgets', description: 'View and manage project budgets and forecasts' },
    ],
  },
  {
    id: 'integrations',
    name: 'Integration Permissions',
    description: 'Manage system integrations and company data',
    iconName: 'Settings',
    iconColor: '', // Will use scope color
    scope: 'admin',
    permissions: [
      { id: 'integrations.view', name: 'View Integrations', description: 'View integration configurations and status' },
      { id: 'integrations.manage', name: 'Manage Integrations', description: 'Configure and manage system integrations' },
      { id: 'integrations.sync', name: 'Sync Data', description: 'Trigger manual data synchronization' },
      { id: 'companies.view', name: 'View Companies', description: 'View company list and basic information' },
      { id: 'companies.manage', name: 'Manage Companies', description: 'Create, edit, and manage company settings' },
    ],
  },
  {
    id: 'settings',
    name: 'Settings & Administration Permissions',
    description: 'Configure system settings and manage users',
    iconName: 'Settings',
    iconColor: '', // Will use scope color
    scope: 'admin',
    permissions: [
      { id: 'settings.view', name: 'View Settings', description: 'View all settings (read-only access)' },
      { id: 'settings.manage_general', name: 'Manage General Settings', description: 'Configure general application settings' },

      { id: 'settings.manage_deadlines', name: 'Manage Deadlines', description: 'Configure timesheet submission deadlines' },
      { id: 'settings.manage_policies', name: 'Manage Policy Settings', description: 'Configure policy violation rules and monitoring' },
      { id: 'settings.manage_utilization', name: 'Manage Utilization Settings', description: 'Configure utilization targets and thresholds' },
      { id: 'settings.manage_roles', name: 'Manage Roles & Permissions', description: 'Create and manage user roles and permissions' },
      { id: 'settings.manage_approval_workflows', name: 'Manage Approval Workflows', description: 'Configure approval workflows and stages' },
      { id: 'settings.manage_notifications', name: 'Manage Notifications', description: 'Configure notification preferences and rules' },
      { id: 'settings.view_audit', name: 'View Audit Logs', description: 'View system audit logs and change history' },
      { id: 'settings.manage_security', name: 'Manage Security Settings', description: 'Manage security and access control settings' },
    ],
  },
  {
    id: 'data',
    name: 'Data Operations Permissions',
    description: 'Import, export, and bulk data operations',
    iconName: 'Settings',
    iconColor: '', // Will use scope color
    scope: 'admin',
    permissions: [
      { id: 'data.import', name: 'Import Data', description: 'Import timesheet and employee data' },
      { id: 'data.export', name: 'Export Data', description: 'Export data to various formats' },
      { id: 'data.bulk_operations', name: 'Bulk Operations', description: 'Perform bulk data operations' },
    ],
  },
];

// Mock users data
interface UserData {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'active' | 'inactive';
  avatar?: string;
}

const mockUsers: UserData[] = [
  { id: '1', name: 'John Smith', email: 'john.smith@company.com', roleId: '1', roleName: 'Administrator', status: 'active' },
  { id: '2', name: 'Sarah Johnson', email: 'sarah.johnson@company.com', roleId: '1', roleName: 'Administrator', status: 'active' },
  { id: '3', name: 'Michael Chen', email: 'michael.chen@company.com', roleId: '1', roleName: 'Administrator', status: 'active' },
  { id: '4', name: 'Emily Davis', email: 'emily.davis@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '5', name: 'David Wilson', email: 'david.wilson@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '6', name: 'Lisa Anderson', email: 'lisa.anderson@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '7', name: 'James Martinez', email: 'james.martinez@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '8', name: 'Jennifer Taylor', email: 'jennifer.taylor@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '9', name: 'Robert Brown', email: 'robert.brown@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '10', name: 'Maria Garcia', email: 'maria.garcia@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '11', name: 'William Lee', email: 'william.lee@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '12', name: 'Patricia White', email: 'patricia.white@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '13', name: 'Thomas Harris', email: 'thomas.harris@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '14', name: 'Linda Clark', email: 'linda.clark@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '15', name: 'Christopher Lewis', email: 'christopher.lewis@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '16', name: 'Barbara Robinson', email: 'barbara.robinson@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '17', name: 'Daniel Walker', email: 'daniel.walker@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '18', name: 'Nancy Hall', email: 'nancy.hall@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
];

export const EditRolePage: React.FC<EditRolePageProps> = ({ onNavigate }) => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [enabledPermissions, setEnabledPermissions] = useState<Set<string>>(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [users, setUsers] = useState<UserData[]>([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [newRoleId, setNewRoleId] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [allRoles, setAllRoles] = useState<UserRoleListItem[]>([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [permissionChanges, setPermissionChanges] = useState<{
    added: string[];
    removed: string[];
  }>({ added: [], removed: [] });
  
  // Timesheet Access Settings
  const [viewTimesheetsAccess, setViewTimesheetsAccess] = useState<string>('own');
  const [editTimesheetsAccess, setEditTimesheetsAccess] = useState<string>('own');
  const [allowDeleteTimesheets, setAllowDeleteTimesheets] = useState(false);
  const [allowDuplicateTimesheets, setAllowDuplicateTimesheets] = useState(false);
  const [allowRecallTimesheet, setAllowRecallTimesheet] = useState(false);
  
  // Smart Features Permissions (role-level)
  const [enableCopyFromLastWeek, setEnableCopyFromLastWeek] = useState(false);
  const [enableCopyDescription, setEnableCopyDescription] = useState(false);
  const [enableCopyOnlyBillable, setEnableCopyOnlyBillable] = useState(false);
  const [enableAutoSmartFill, setEnableAutoSmartFill] = useState(false);
  const [enableAiInsights, setEnableAiInsights] = useState(false);

  // Approval Homepage permission
  const [viewApprovalDashboard, setViewApprovalDashboard] = useState(false);

  // Timesheet Approval permissions
  const [viewApprovals, setViewApprovals] = useState(false);
  const [editTimesheetsApproval, setEditTimesheetsApproval] = useState(false);
  const [rejectTimesheets, setRejectTimesheets] = useState(false);
  const [compulsoryRejectionReason, setCompulsoryRejectionReason] = useState(false);
  const [bulkApprove, setBulkApprove] = useState(false);
  const [delegateApproval, setDelegateApproval] = useState(false);
  const [viewPolicyViolations, setViewPolicyViolations] = useState(false);
  const [viewDetectedIssues, setViewDetectedIssues] = useState(false);

  // Report Access permissions
  // Timesheet Reports
  const [enableTimesheetReport, setEnableTimesheetReport] = useState(false);
  const [viewTimesheetReportAccess, setViewTimesheetReportAccess] = useState<string>('own');
  const [allowTimesheetReportExport, setAllowTimesheetReportExport] = useState(false);
  
  // Missing Timesheet Report
  const [enableMissingTimesheetReport, setEnableMissingTimesheetReport] = useState(false);
  const [viewMissingTimesheetReportAccess, setViewMissingTimesheetReportAccess] = useState<string>('own');
  const [allowMissingTimesheetReportExport, setAllowMissingTimesheetReportExport] = useState(false);
  
  // Utilisation Report
  const [enableUtilisationReport, setEnableUtilisationReport] = useState(false);
  const [viewUtilisationReportAccess, setViewUtilisationReportAccess] = useState<string>('own');
  const [allowUtilisationReportExport, setAllowUtilisationReportExport] = useState(false);
  
  // Billable Hours Report removed — belongs in the Projects module of the PSA suite, not the Timesheet module.



  // Employee Directory permissions
  const [viewEmployeeDirectory, setViewEmployeeDirectory] = useState(false);
  const [viewEmployeeDetails, setViewEmployeeDetails] = useState(false);

  // Data & Integration - Integrations permissions
  const [viewIntegrations, setViewIntegrations] = useState(false);
  const [manageIntegrations, setManageIntegrations] = useState(false);

  // Company Management permissions
  const [viewCompanies, setViewCompanies] = useState(false);
  const [manageCompanies, setManageCompanies] = useState(false);

  // System Settings permissions
  const [manageGeneralSettings, setManageGeneralSettings] = useState<'view' | 'configure'>('view');

  const [manageDeadlines, setManageDeadlines] = useState(false);
  const [managePolicySettings, setManagePolicySettings] = useState(false);
  const [manageUtilisationSettings, setManageUtilisationSettings] = useState(false);
  const [manageTimeEntrySettings, setManageTimeEntrySettings] = useState(false);
  const [manageWorkTypeSettings, setManageWorkTypeSettings] = useState(false);
  const [manageApprovalsSettings, setManageApprovalsSettings] = useState(false);
  const [manageNotifications, setManageNotifications] = useState(false);
  const [manageRolesPermissions, setManageRolesPermissions] = useState(false);
  // manageScreenDataModes — hidden from UI pending Settings menu visibility decision (see docs/features/roles-and-permissions.md)
  const [manageScreenDataModes, setManageScreenDataModes] = useState(false);
  
  // Get global settings to determine which features are available
  const globalSettings = useGlobalSettings();

  // Get screen mode for live/demo data source
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('roles-permissions');
  const userRoleService = useMemo(() => getUserRoleService(screenMode), [screenMode]);

  // Determine if we're in create mode
  const isCreateMode = !roleId || roleId === 'create';

  useEffect(() => {
    const loadRoleData = async () => {
      // Load all roles for the transfer dialog
      try {
        const roles = await userRoleService.getAllUserRoles();
        setAllRoles(roles);
      } catch (error) {
        console.error('Error loading all roles:', error);
      }

      // Set currentEditingRoleId in localStorage so PermissionContext can use it for testing
      // This allows users to test permissions while editing a role in demo mode
      if (!isCreateMode && roleId) {
        localStorage.setItem('currentEditingRoleId', roleId);
        // Dispatch event to notify PermissionContext
        window.dispatchEvent(new Event('permissionsUpdated'));
      }

      // Load role data only in edit mode
      if (!isCreateMode && roleId) {
        try {
          const role = await userRoleService.getUserRoleById(Number(roleId));
          setRoleName(role.name);
          setRoleDescription(role.description || '');
          setIsSystem(role.isSystem);
          // Platform roles must always be active
          setIsActive(role.isSystem ? true : role.isActive);
          
          // Load permissions if available
          if (role.permissions) {
            loadPermissionsFromData(role.permissions);
          }
          
          // Load users for this role from the API
          try {
            const employees = await userRoleService.getEmployeesByRoleId(Number(roleId));
            // Map API response to UserData format
            const roleUsers: UserData[] = employees.map(emp => ({
              id: String(emp.id),
              name: emp.name,
              email: emp.email,
              roleId: roleId,
              roleName: emp.roleName || roleName,
              status: emp.status === 'active' ? 'active' : 'inactive',
            }));
            setUsers(roleUsers);
          } catch (error) {
            console.error('Error loading users for role:', error);
            // Fall back to mock users in case of error
            const roleUsers = mockUsers.filter(user => user.roleId === roleId);
            setUsers(roleUsers);
          }
        } catch (error) {
          console.error('Error loading role:', error);
        }
      } else if (isCreateMode) {
        // Check if this is a duplication
        const duplicationDataStr = sessionStorage.getItem('roleDuplicationData');
        
        if (duplicationDataStr) {
          try {
            const duplicationData = JSON.parse(duplicationDataStr);
            const sourceRoleId = duplicationData.sourceRoleId;
            
            // Load source role permissions from the service
            try {
              const sourceRole = await userRoleService.getUserRoleById(sourceRoleId);
              if (sourceRole.permissions) {
                loadPermissionsFromData(sourceRole.permissions);
              }
              
              // Set suggested name
              setRoleName(`${duplicationData.sourceRoleName} (Copy)`);
              setRoleDescription('');
              
            } catch (error) {
              console.error('Error loading source role for duplication:', error);
            }
            
            // Clear the duplication data from sessionStorage
            sessionStorage.removeItem('roleDuplicationData');
          } catch (error) {
            console.error('Error loading duplication data:', error);
          }
        } else {
          // Normal create mode - start with empty form
          setRoleName('');
          setRoleDescription('');
          setEnabledPermissions(new Set());
          setIsSystem(false);
          setIsActive(true);
          setUsers([]);
        }
      }
    };
    
    loadRoleData();
  }, [roleId, isCreateMode, userRoleService]);

  // Cleanup currentEditingRoleId when component unmounts
  useEffect(() => {
    return () => {
      // Clear the currentEditingRoleId when leaving the edit page
      localStorage.removeItem('currentEditingRoleId');
      // Dispatch event to notify PermissionContext to reload with default permissions
      window.dispatchEvent(new Event('permissionsUpdated'));
    };
  }, []);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleTogglePermission = (permissionId: string) => {
    setEnabledPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
    setHasUnsavedChanges(true);
  };

  const handleToggleCategory = (category: PermissionCategory, enabled: boolean) => {
    setEnabledPermissions(prev => {
      const newSet = new Set(prev);
      category.permissions.forEach(permission => {
        if (enabled) {
          newSet.add(permission.id);
        } else {
          newSet.delete(permission.id);
        }
      });
      return newSet;
    });
    setHasUnsavedChanges(true);
  };

  const isCategoryFullyEnabled = (category: PermissionCategory) => {
    return category.permissions.every(permission => enabledPermissions.has(permission.id));
  };

  const getCategoryEnabledCount = (category: PermissionCategory) => {
    return category.permissions.filter(permission => enabledPermissions.has(permission.id)).length;
  };

  // Calculate total permissions count based on actual state variables
  const getTotalPermissionsCount = () => {
    let count = 0;
    
    // Personal Dashboard (from enabledPermissions)
    if (enabledPermissions.has('dashboard.view_own')) count++;
    
    // Time Tracking - Timesheet Access
    if (viewTimesheetsAccess !== 'none') count++;
    if (editTimesheetsAccess !== 'none') count++;
    if (allowDeleteTimesheets) count++;
    if (allowDuplicateTimesheets) count++;
    if (allowRecallTimesheet) count++;
    
    // Time Tracking - Smart Features
    if (enableCopyFromLastWeek) count++;
    if (enableCopyDescription) count++;
    if (enableCopyOnlyBillable) count++;
    if (enableAutoSmartFill) count++;
    if (enableAiInsights) count++;
    
    // Approval - Approval Dashboard
    if (viewApprovalDashboard) count++;
    
    // Approval - Timesheet Approval
    if (viewApprovals) count++;
    if (editTimesheetsApproval) count++;
    if (rejectTimesheets) count++;
    if (compulsoryRejectionReason) count++;
    if (bulkApprove) count++;
    if (delegateApproval) count++;
    if (viewPolicyViolations) count++;
    if (viewDetectedIssues) count++;
    
    // Reports & Analytics - Report Access
    if (enableTimesheetReport) count++;
    if (enableMissingTimesheetReport) count++;
    if (enableUtilisationReport) count++;

    // Management - Employee Directory
    if (viewEmployeeDirectory) count++;
    if (viewEmployeeDetails) count++;
    
    // Data & Integration - Integrations
    if (viewIntegrations) count++;
    if (manageIntegrations) count++;
    
    // Data & Integration - Company Management
    if (viewCompanies) count++;
    if (manageCompanies) count++;
    
    // Administration - System Settings
    if (manageGeneralSettings === 'configure') count++;

    if (manageDeadlines) count++;
    if (managePolicySettings) count++;
    if (manageUtilisationSettings) count++;
    if (manageTimeEntrySettings) count++;
    if (manageWorkTypeSettings) count++;
    if (manageApprovalsSettings) count++;
    if (manageNotifications) count++;
    if (manageRolesPermissions) count++;
    // manageScreenDataModes excluded from count — hidden from UI
    
    return count;
  };

  // Calculate tab-specific permission counts for badges
  const getTabPermissionCount = (tabId: string): number => {
    switch (tabId) {
      case 'time-tracking':
        return [
          enabledPermissions.has('dashboard.view_own'),
          viewTimesheetsAccess !== 'none',
          editTimesheetsAccess !== 'none',
          allowDeleteTimesheets,
          allowDuplicateTimesheets,
          allowRecallTimesheet,
          enableCopyFromLastWeek,
          enableCopyDescription,
          enableCopyOnlyBillable,
          enableAutoSmartFill,
          enableAiInsights,
        ].filter(Boolean).length;
      case 'approval':
        return [
          viewApprovalDashboard,
          viewApprovals,
          editTimesheetsApproval,
          rejectTimesheets,
          compulsoryRejectionReason,
          bulkApprove,
          delegateApproval,
          viewPolicyViolations,
          viewDetectedIssues,
        ].filter(Boolean).length;
      case 'reports':
        return [
          enableTimesheetReport,
          allowTimesheetReportExport,
          enableMissingTimesheetReport,
          allowMissingTimesheetReportExport,
          enableUtilisationReport,
          allowUtilisationReportExport,
        ].filter(Boolean).length;
      case 'management':
        return [
          viewEmployeeDirectory,
          viewEmployeeDetails,
        ].filter(Boolean).length;
      case 'data':
        return [
          viewIntegrations,
          manageIntegrations,
          viewCompanies,
          manageCompanies,
        ].filter(Boolean).length;
      case 'administration':
        return [
          manageGeneralSettings === 'configure',
          manageDeadlines,
          managePolicySettings,
          manageUtilisationSettings,
          manageTimeEntrySettings,
          manageWorkTypeSettings,
          manageApprovalsSettings,
          manageNotifications,
          manageRolesPermissions,
          manageScreenDataModes,
        ].filter(Boolean).length;
      default:
        return 0;
    }
  };

  // Calculate category-specific permission counts for the Summary
  // Category IDs match permissionCategories: dashboard, timesheet, approvals, reports, team, projects, integrations, settings, data
  const getCategorySummaryCount = (categoryId: string): { enabled: number; total: number } => {
    switch (categoryId) {
      case 'dashboard':
        return {
          enabled: enabledPermissions.has('dashboard.view_own') ? 1 : 0,
          total: 3
        };
      case 'timesheet':
        return {
          enabled: [
            viewTimesheetsAccess !== 'none',
            editTimesheetsAccess !== 'none',
            allowDeleteTimesheets,
            allowDuplicateTimesheets,
            allowRecallTimesheet,
            enableCopyFromLastWeek || enableCopyDescription || enableCopyOnlyBillable || enableAutoSmartFill || enableAiInsights
          ].filter(Boolean).length,
          total: 6
        };
      case 'projects':
        // Projects tab not implemented yet - use enabledPermissions
        return {
          enabled: getCategoryEnabledCount(permissionCategories.find(c => c.id === 'projects')!),
          total: 8
        };
      case 'approvals':
        return {
          enabled: [
            viewApprovalDashboard,
            viewApprovals || editTimesheetsApproval || rejectTimesheets,
            bulkApprove || viewPolicyViolations || viewDetectedIssues
          ].filter(Boolean).length,
          total: 7
        };
      case 'reports':
        return {
          enabled: [
            enableTimesheetReport,
            enableMissingTimesheetReport,
            enableUtilisationReport,
          ].filter(Boolean).length,
          total: 5
        };
      case 'team':
        return {
          enabled: [
            viewEmployeeDirectory || viewEmployeeDetails
          ].filter(Boolean).length,
          total: 6
        };
      case 'integrations':
        return {
          enabled: [
            viewIntegrations || manageIntegrations,
            viewCompanies || manageCompanies
          ].filter(Boolean).length,
          total: 5
        };
      case 'settings':
        return {
          enabled: [
            manageGeneralSettings === 'configure',
            manageDeadlines || managePolicySettings || manageUtilisationSettings || manageTimeEntrySettings || manageWorkTypeSettings,
            manageRolesPermissions || manageNotifications
          ].filter(Boolean).length,
          total: 11 // manageScreenDataModes excluded — hidden from UI
        };
      case 'data':
        return {
          enabled: 0,
          total: 3
        };
      default:
        return { enabled: 0, total: 0 };
    }
  };

  const handleSave = async () => {
    // Validate required fields
    if (!roleName.trim()) {
      setNameError('Role name is required');
      return;
    }
    
    setNameError('');
    
    // Calculate permission changes for the confirmation dialog
    if (!isCreateMode && roleId) {
      // In edit mode, show confirmation with changes
      calculatePermissionChanges();
      setShowSaveConfirmation(true);
    } else {
      // In create mode, save directly
      await performSave();
    }
  };

  const calculatePermissionChanges = async () => {
    if (!roleId) return;
    
    try {
      const currentRole = await userRoleService.getUserRoleById(Number(roleId));
      const currentPermissions = new Set(currentRole.permissions?.permissions || []);
      const newPermissions = new Set(enabledPermissions);
      
      const added: string[] = [];
      const removed: string[] = [];
      
      // Check for added permissions
      newPermissions.forEach(perm => {
        if (!currentPermissions.has(perm)) {
          added.push(perm);
        }
      });
      
      // Check for removed permissions
      currentPermissions.forEach(perm => {
        if (!newPermissions.has(perm)) {
          removed.push(perm);
        }
      });
      
      setPermissionChanges({ added, removed });
    } catch (error) {
      console.error('Error calculating permission changes:', error);
      setPermissionChanges({ added: [], removed: [] });
    }
  };

  const performSave = async () => {
    setIsSaving(true);
    setShowSaveConfirmation(false);
    
    // Enforce platform role status: Platform roles must always be active
    const finalIsActive = isSystem ? true : isActive;
    
    if (isSystem && !isActive) {
      console.warn('Platform roles cannot be deactivated. Status has been set to active.');
    }
    
    // Build permissions object for API
    const permissionsData: UserRolePermissions = {
      permissions: Array.from(enabledPermissions),
      timesheetAccess: {
        viewAccess: viewTimesheetsAccess as 'none' | 'own' | 'team' | 'all',
        editAccess: editTimesheetsAccess as 'none' | 'own' | 'team' | 'all',
        allowDelete: allowDeleteTimesheets,
        allowDuplicate: allowDuplicateTimesheets,
        allowRecall: allowRecallTimesheet,
      },
      smartFeaturesPermissions: {
        enableCopyFromLastWeek,
        enableCopyDescription,
        enableCopyOnlyBillable,
        enableAutoSmartFill,
        enableAiInsights,
      },
      approvalPermissions: {
        viewDashboard: viewApprovalDashboard,
      },
      timesheetApprovalPermissions: {
        viewApprovals,
        editTimesheets: editTimesheetsApproval,
        rejectTimesheets,
        compulsoryRejectionReason,
        bulkApprove,
        delegateApproval,
        viewPolicyViolations,
        viewDetectedIssues,
      },
      reportAccessPermissions: {
        timesheetReport: {
          enabled: enableTimesheetReport,
          viewAccess: viewTimesheetReportAccess,
          allowExport: allowTimesheetReportExport,
        },
        missingTimesheetReport: {
          enabled: enableMissingTimesheetReport,
          viewAccess: viewMissingTimesheetReportAccess,
          allowExport: allowMissingTimesheetReportExport,
        },
        utilisationReport: {
          enabled: enableUtilisationReport,
          viewAccess: viewUtilisationReportAccess,
          allowExport: allowUtilisationReportExport,
        },
        // billableHoursReport removed — belongs in the Projects module of the PSA suite.
      },
      employeeDirectoryPermissions: {
        viewDirectory: viewEmployeeDirectory,
        viewDetails: viewEmployeeDetails,
      },
      integrationsPermissions: {
        viewIntegrations,
        manageIntegrations,
      },
      companyManagementPermissions: {
        viewCompanies,
        manageCompanies,
      },
      systemSettingsPermissions: {
        manageGeneralSettings,
        manageDeadlines,
        managePolicySettings,
        manageUtilisationSettings,
        manageTimeEntrySettings,
        manageWorkTypeSettings,
        manageApprovalsSettings,
        manageNotifications,
        manageRolesPermissions: isSystem ? true : manageRolesPermissions,
        manageScreenDataModes,
      },
    };
    
    try {
      if (isCreateMode) {
        // Create new role using the service (works for both live and demo modes)
        const createDto: CreateUserRoleDto = {
          name: roleName,
          description: roleDescription,
          isActive: finalIsActive,
          permissions: permissionsData,
        };
        
        await userRoleService.createUserRole(createDto);
      } else {
        // Update existing role using the service (works for both live and demo modes)
        const updateDto: UpdateUserRoleDto = {
          name: roleName,
          description: roleDescription,
          isActive: finalIsActive,
          permissions: permissionsData,
        };
        
        await userRoleService.updateUserRole(Number(roleId), updateDto);
      }
      
      // Dispatch custom event to notify RolesPermissionsPage
      window.dispatchEvent(new Event('rolesUpdated'));
      
      // Dispatch custom event to notify PermissionContext to reload permissions
      window.dispatchEvent(new Event('permissionsUpdated'));
      
      setIsSaving(false);
      setHasUnsavedChanges(false);
      
      // Show success toast
      toast.success(
        isCreateMode ? "Role created successfully" : "Role updated successfully",
        {
          description: `${roleName} has been ${isCreateMode ? 'created' : 'updated'} with ${enabledPermissions.size} permissions.`,
        }
      );
      
      // Navigate back to roles list
      if (onNavigate) {
        onNavigate('/settings/roles-permissions');
      } else {
        navigate('/settings/roles-permissions');
      }
    } catch (error: unknown) {
      console.error('Error saving role:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save role';
      toast.error('Save failed', {
        description: errorMessage,
      });
      setIsSaving(false);
    }
  };

  const handleTransferRole = (user: UserData) => {
    setSelectedUser(user);
    setNewRoleId('');
    setTransferDialogOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (!selectedUser || !newRoleId || !roleId) return;

    try {
      // Remove user from current role
      await userRoleService.removeEmployeeFromRole(Number(roleId), Number(selectedUser.id));
      
      // Add user to new role
      await userRoleService.assignEmployeeToRole(Number(newRoleId), Number(selectedUser.id));

      // Update the user's role in the local state
      setUsers(prevUsers => prevUsers.filter(u => u.id !== selectedUser.id));
      
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('rolesUpdated'));
      
      setTransferDialogOpen(false);
      setSelectedUser(null);
      setNewRoleId('');
    } catch (error: unknown) {
      console.error('Error transferring user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to transfer user';
      toast.error('Transfer failed', {
        description: errorMessage,
      });
    }
  };

  // Helper function to load permissions from stored data
  const loadPermissionsFromData = (permissionsData: any) => {
    try {
      // Load basic permissions array (includes Personal Dashboard and other category permissions)
      if (permissionsData.permissions && Array.isArray(permissionsData.permissions)) {
        setEnabledPermissions(new Set(permissionsData.permissions));
      }

      // Time Tracking - Timesheet Access
      if (permissionsData.timesheetAccess) {
        setViewTimesheetsAccess(permissionsData.timesheetAccess.viewAccess || 'own');
        setEditTimesheetsAccess(permissionsData.timesheetAccess.editAccess || 'own');
        setAllowDeleteTimesheets(permissionsData.timesheetAccess.allowDelete || false);
        setAllowDuplicateTimesheets(permissionsData.timesheetAccess.allowDuplicate || false);
        setAllowRecallTimesheet(permissionsData.timesheetAccess.allowRecall || false);
      }

      // Time Tracking - Smart Features
      if (permissionsData.smartFeaturesPermissions) {
        setEnableCopyFromLastWeek(permissionsData.smartFeaturesPermissions.enableCopyFromLastWeek || false);
        setEnableCopyDescription(permissionsData.smartFeaturesPermissions.enableCopyDescription || false);
        setEnableCopyOnlyBillable(permissionsData.smartFeaturesPermissions.enableCopyOnlyBillable || false);
        setEnableAutoSmartFill(permissionsData.smartFeaturesPermissions.enableAutoSmartFill || false);
        setEnableAiInsights(permissionsData.smartFeaturesPermissions.enableAiInsights || false);
      }

      // Approval - Approval Dashboard
      setViewApprovalDashboard(permissionsData.approvalPermissions?.viewDashboard || false);

      // Approval - Timesheet Approval
      if (permissionsData.timesheetApprovalPermissions) {
        setViewApprovals(permissionsData.timesheetApprovalPermissions.viewApprovals || false);
        setEditTimesheetsApproval(permissionsData.timesheetApprovalPermissions.editTimesheets || false);
        setRejectTimesheets(permissionsData.timesheetApprovalPermissions.rejectTimesheets || false);
        setCompulsoryRejectionReason(permissionsData.timesheetApprovalPermissions.compulsoryRejectionReason || false);
        setBulkApprove(permissionsData.timesheetApprovalPermissions.bulkApprove || false);
        setDelegateApproval(permissionsData.timesheetApprovalPermissions.delegateApproval || false);
        setViewPolicyViolations(permissionsData.timesheetApprovalPermissions.viewPolicyViolations || false);
        setViewDetectedIssues(permissionsData.timesheetApprovalPermissions.viewDetectedIssues || false);
      }

      // Reports & Analytics - Report Access
      if (permissionsData.reportAccessPermissions) {
        setEnableTimesheetReport(permissionsData.reportAccessPermissions.timesheetReport?.enabled || false);
        setViewTimesheetReportAccess(permissionsData.reportAccessPermissions.timesheetReport?.viewAccess || 'own');
        setAllowTimesheetReportExport(permissionsData.reportAccessPermissions.timesheetReport?.allowExport || false);

        setEnableMissingTimesheetReport(permissionsData.reportAccessPermissions.missingTimesheetReport?.enabled || false);
        setViewMissingTimesheetReportAccess(permissionsData.reportAccessPermissions.missingTimesheetReport?.viewAccess || 'own');
        setAllowMissingTimesheetReportExport(permissionsData.reportAccessPermissions.missingTimesheetReport?.allowExport || false);

        setEnableUtilisationReport(permissionsData.reportAccessPermissions.utilisationReport?.enabled || false);
        setViewUtilisationReportAccess(permissionsData.reportAccessPermissions.utilisationReport?.viewAccess || 'own');
        setAllowUtilisationReportExport(permissionsData.reportAccessPermissions.utilisationReport?.allowExport || false);

        // billableHoursReport removed — belongs in the Projects module of the PSA suite.
      }

      // Reports & Analytics - Analytics & Insights section removed from Timesheet module

      // Management - Employee Directory
      if (permissionsData.employeeDirectoryPermissions) {
        setViewEmployeeDirectory(permissionsData.employeeDirectoryPermissions.viewDirectory || false);
        setViewEmployeeDetails(permissionsData.employeeDirectoryPermissions.viewDetails || false);
      }

      // Data & Integration - Integrations
      if (permissionsData.integrationsPermissions) {
        setViewIntegrations(permissionsData.integrationsPermissions.viewIntegrations || false);
        setManageIntegrations(permissionsData.integrationsPermissions.manageIntegrations || false);
      }

      // Data & Integration - Company Management
      if (permissionsData.companyManagementPermissions) {
        setViewCompanies(permissionsData.companyManagementPermissions.viewCompanies || false);
        setManageCompanies(permissionsData.companyManagementPermissions.manageCompanies || false);
      }

      // Administration - System Settings
      if (permissionsData.systemSettingsPermissions) {
        setManageGeneralSettings(permissionsData.systemSettingsPermissions.manageGeneralSettings || 'view');

        setManageDeadlines(permissionsData.systemSettingsPermissions.manageDeadlines || false);
        setManagePolicySettings(permissionsData.systemSettingsPermissions.managePolicySettings || false);
        setManageUtilisationSettings(permissionsData.systemSettingsPermissions.manageUtilisationSettings || false);
        setManageTimeEntrySettings(permissionsData.systemSettingsPermissions.manageTimeEntrySettings || false);
        setManageWorkTypeSettings(permissionsData.systemSettingsPermissions.manageWorkTypeSettings || false);
        setManageApprovalsSettings(permissionsData.systemSettingsPermissions.manageApprovalsSettings || false);
        setManageNotifications(permissionsData.systemSettingsPermissions.manageNotifications || false);
        setManageRolesPermissions(permissionsData.systemSettingsPermissions.manageRolesPermissions || false);
        setManageScreenDataModes(permissionsData.systemSettingsPermissions.manageScreenDataModes || false);
      }
    } catch (error) {
      console.error('Error loading permissions from data:', error);
    }
  };

  const handleCancel = () => {
    if (onNavigate) {
      onNavigate('/settings/roles-permissions');
    } else {
      navigate('/settings/roles-permissions');
    }
  };

  /**
   * Apply the default permission template for a system role.
   * Only available for system roles (ids 1, 2, 3 = Administrator, User, Approver).
   * Populates all permission state from the DEFAULT_ROLE_PERMISSIONS_MAP constant.
   */
  const handleApplyDefaultTemplate = useCallback(() => {
    if (!roleId) return;
    const defaults = DEFAULT_ROLE_PERMISSIONS_MAP[Number(roleId)];
    if (!defaults) return;

    // Personal homepage + any other string-code permissions
    setEnabledPermissions(new Set(defaults.permissions ?? []));

    // Timesheet Access
    if (defaults.timesheetAccess) {
      setViewTimesheetsAccess(defaults.timesheetAccess.viewAccess);
      setEditTimesheetsAccess(defaults.timesheetAccess.editAccess);
      setAllowDeleteTimesheets(defaults.timesheetAccess.allowDelete);
      setAllowDuplicateTimesheets(defaults.timesheetAccess.allowDuplicate);
      setAllowRecallTimesheet(defaults.timesheetAccess.allowRecall);
    }

    // Smart Features
    if (defaults.smartFeaturesPermissions) {
      setEnableCopyFromLastWeek(defaults.smartFeaturesPermissions.enableCopyFromLastWeek);
      setEnableCopyDescription(defaults.smartFeaturesPermissions.enableCopyDescription);
      setEnableCopyOnlyBillable(defaults.smartFeaturesPermissions.enableCopyOnlyBillable);
      setEnableAutoSmartFill(defaults.smartFeaturesPermissions.enableAutoSmartFill);
      setEnableAiInsights(defaults.smartFeaturesPermissions.enableAiInsights);
    }

    // Approval Homepage
    setViewApprovalDashboard(defaults.approvalPermissions?.viewDashboard ?? false);

    // Timesheet Approval
    if (defaults.timesheetApprovalPermissions) {
      setViewApprovals(defaults.timesheetApprovalPermissions.viewApprovals);
      setEditTimesheetsApproval(defaults.timesheetApprovalPermissions.editTimesheets);
      setRejectTimesheets(defaults.timesheetApprovalPermissions.rejectTimesheets);
      setCompulsoryRejectionReason(defaults.timesheetApprovalPermissions.compulsoryRejectionReason);
      setBulkApprove(defaults.timesheetApprovalPermissions.bulkApprove);
      setDelegateApproval(defaults.timesheetApprovalPermissions.delegateApproval);
      setViewPolicyViolations(defaults.timesheetApprovalPermissions.viewPolicyViolations);
      setViewDetectedIssues(defaults.timesheetApprovalPermissions.viewDetectedIssues);
    }

    // Report Access
    if (defaults.reportAccessPermissions) {
      setEnableTimesheetReport(defaults.reportAccessPermissions.timesheetReport?.enabled ?? false);
      setViewTimesheetReportAccess(defaults.reportAccessPermissions.timesheetReport?.viewAccess ?? 'own');
      setAllowTimesheetReportExport(defaults.reportAccessPermissions.timesheetReport?.allowExport ?? false);
      setEnableMissingTimesheetReport(defaults.reportAccessPermissions.missingTimesheetReport?.enabled ?? false);
      setViewMissingTimesheetReportAccess(defaults.reportAccessPermissions.missingTimesheetReport?.viewAccess ?? 'own');
      setAllowMissingTimesheetReportExport(defaults.reportAccessPermissions.missingTimesheetReport?.allowExport ?? false);
      setEnableUtilisationReport(defaults.reportAccessPermissions.utilisationReport?.enabled ?? false);
      setViewUtilisationReportAccess(defaults.reportAccessPermissions.utilisationReport?.viewAccess ?? 'own');
      setAllowUtilisationReportExport(defaults.reportAccessPermissions.utilisationReport?.allowExport ?? false);
      // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    }

    // Analytics section removed from Timesheet module — no defaults to set

    // Employee Directory
    if (defaults.employeeDirectoryPermissions) {
      setViewEmployeeDirectory(defaults.employeeDirectoryPermissions.viewDirectory);
      setViewEmployeeDetails(defaults.employeeDirectoryPermissions.viewDetails);
    }

    // Integrations
    if (defaults.integrationsPermissions) {
      setViewIntegrations(defaults.integrationsPermissions.viewIntegrations);
      setManageIntegrations(defaults.integrationsPermissions.manageIntegrations);
    }

    // Company Management
    if (defaults.companyManagementPermissions) {
      setViewCompanies(defaults.companyManagementPermissions.viewCompanies);
      setManageCompanies(defaults.companyManagementPermissions.manageCompanies);
    }

    // System Settings
    if (defaults.systemSettingsPermissions) {
      setManageGeneralSettings(defaults.systemSettingsPermissions.manageGeneralSettings);
      setManageDeadlines(defaults.systemSettingsPermissions.manageDeadlines);
      setManagePolicySettings(defaults.systemSettingsPermissions.managePolicySettings);
      setManageUtilisationSettings(defaults.systemSettingsPermissions.manageUtilisationSettings);
      setManageTimeEntrySettings(defaults.systemSettingsPermissions.manageTimeEntrySettings ?? false);
      setManageWorkTypeSettings(defaults.systemSettingsPermissions.manageWorkTypeSettings ?? false);
      setManageApprovalsSettings(defaults.systemSettingsPermissions.manageApprovalsSettings);
      setManageNotifications(defaults.systemSettingsPermissions.manageNotifications);
      setManageRolesPermissions(defaults.systemSettingsPermissions.manageRolesPermissions);
      setManageScreenDataModes(defaults.systemSettingsPermissions.manageScreenDataModes);
    }

    setHasUnsavedChanges(true);
    toast.success('Default template applied. Review and save to confirm.');
  }, [roleId]);

  // Select All / Deselect All helper functions for Time Tracking tab
  const handleSelectAllTimeTracking = () => {
    // Add Personal Homepage permission
    const newPermissions = new Set(enabledPermissions);
    newPermissions.add('dashboard.view_own');
    setEnabledPermissions(newPermissions);
    
    // Set Timesheet Access permissions
    setAllowRecallTimesheet(true);
    setAllowDuplicateTimesheets(true);
    setAllowDeleteTimesheets(true);
    
    // Set Smart Features permissions (only if enabled globally)
    setEnableCopyFromLastWeek(globalSettings.enableCopyPreviousWeek);
    setEnableCopyDescription(globalSettings.copyDescriptions);
    setEnableCopyOnlyBillable(globalSettings.copyOnlyBillable);
    setEnableAutoSmartFill(globalSettings.enableAutoSmartFill);
    setEnableAiInsights(globalSettings.enableAiInsights);
    
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllTimeTracking = () => {
    // Remove Personal Homepage permission
    const newPermissions = new Set(enabledPermissions);
    newPermissions.delete('dashboard.view_own');
    setEnabledPermissions(newPermissions);
    
    // Clear Timesheet Access permissions
    setAllowRecallTimesheet(false);
    setAllowDuplicateTimesheets(false);
    setAllowDeleteTimesheets(false);
    
    // Clear Smart Features permissions
    setEnableCopyFromLastWeek(false);
    setEnableCopyDescription(false);
    setEnableCopyOnlyBillable(false);
    setEnableAutoSmartFill(false);
    setEnableAiInsights(false);
    
    setHasUnsavedChanges(true);
  };

  // Select All / Deselect All helper functions for Approval tab
  const handleSelectAllApproval = () => {
    setViewApprovalDashboard(true);
    setViewApprovals(true);
    setEditTimesheetsApproval(true);
    setRejectTimesheets(true);
    setCompulsoryRejectionReason(true);
    setBulkApprove(true);
    setDelegateApproval(true);
    setViewPolicyViolations(true);
    setViewDetectedIssues(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllApproval = () => {
    setViewApprovalDashboard(false);
    setViewApprovals(false);
    setEditTimesheetsApproval(false);
    setRejectTimesheets(false);
    setCompulsoryRejectionReason(false);
    setBulkApprove(false);
    setDelegateApproval(false);
    setViewPolicyViolations(false);
    setViewDetectedIssues(false);
    setHasUnsavedChanges(true);
  };

  // Select All / Deselect All helper functions for Reports & Analytics tab
  const handleSelectAllReports = () => {
    setEnableTimesheetReport(true);
    setAllowTimesheetReportExport(true);
    setEnableMissingTimesheetReport(true);
    setAllowMissingTimesheetReportExport(true);
    setEnableUtilisationReport(true);
    setAllowUtilisationReportExport(true);
    // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllReports = () => {
    setEnableTimesheetReport(false);
    setAllowTimesheetReportExport(false);
    setEnableMissingTimesheetReport(false);
    setAllowMissingTimesheetReportExport(false);
    setEnableUtilisationReport(false);
    setAllowUtilisationReportExport(false);
    // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    setHasUnsavedChanges(true);
  };

  // Select All / Deselect All helper functions for Management tab
  const handleSelectAllManagement = () => {
    setViewEmployeeDirectory(true);
    setViewEmployeeDetails(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllManagement = () => {
    setViewEmployeeDirectory(false);
    setViewEmployeeDetails(false);
    setHasUnsavedChanges(true);
  };

  // Select All / Deselect All helper functions for Data & Integration tab
  const handleSelectAllDataIntegration = () => {
    setViewIntegrations(true);
    setManageIntegrations(true);
    setViewCompanies(true);
    setManageCompanies(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllDataIntegration = () => {
    setViewIntegrations(false);
    setManageIntegrations(false);
    setViewCompanies(false);
    setManageCompanies(false);
    setHasUnsavedChanges(true);
  };

  // Select All / Deselect All helper functions for Administration tab
  const handleSelectAllAdministration = () => {
    setManageGeneralSettings('configure');
    setManageDeadlines(true);
    setManagePolicySettings(true);
    setManageUtilisationSettings(true);
    setManageTimeEntrySettings(true);
    setManageWorkTypeSettings(true);
    setManageApprovalsSettings(true);
    setManageNotifications(true);
    setManageRolesPermissions(true);
    setManageScreenDataModes(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllAdministration = () => {
    setManageGeneralSettings('view');
    setManageDeadlines(false);
    setManagePolicySettings(false);
    setManageUtilisationSettings(false);
    setManageTimeEntrySettings(false);
    setManageWorkTypeSettings(false);
    setManageApprovalsSettings(false);
    setManageNotifications(false);
    // System roles must always retain manageRolesPermissions
    if (!isSystem) {
      setManageRolesPermissions(false);
    }
    setManageScreenDataModes(false);
    setHasUnsavedChanges(true);
  };

  return (
    <div className="h-full flex flex-col">

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-6 pb-24">{/* Added bottom padding to prevent content from being hidden by fixed footer */}
          {/* Header */}
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <button
                onClick={handleCancel}
                className="hover:text-foreground transition-colors"
              >
                Roles & Permissions
              </button>
              <span>/</span>
              <span>{isCreateMode ? 'Create Role' : 'Edit Role'}</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isCreateMode ? 'Create New Role' : (roleName || 'Edit Role')}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isCreateMode ? 'Set up a new role with specific permissions and access levels' : 'Configure role details and permissions'}
            </p>
          </div>

          {/* Unsaved Changes Alert */}
          {hasUnsavedChanges && (
            <Alert variant="warning" showIcon={false}>
              <AlertDescription>
                You have unsaved changes. Click "{isCreateMode ? 'Create Role' : 'Save Settings'}" at the bottom to apply your changes.
              </AlertDescription>
            </Alert>
          )}

          {/* System role info */}
          {isSystem && (
            <Alert showIcon={false}>
              <AlertDescription>
                <span className="font-medium">Platform Role</span> — You can modify permissions but cannot delete this role.
              </AlertDescription>
            </Alert>
          )}

          {/* Tabbed Interface */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-8 lg:w-auto lg:inline-flex">
          <TabsTrigger value="details" className="gap-2">
            <User className="h-4 w-4" />
            Details
          </TabsTrigger>
          <TabsTrigger value="time-tracking" className="gap-2">
            <Clock className="h-4 w-4" />
            Time Tracking
            {getTabPermissionCount('time-tracking') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                {getTabPermissionCount('time-tracking')}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approval" className="gap-2">
            <CheckSquare className="h-4 w-4" />
            Approval
            {getTabPermissionCount('approval') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                {getTabPermissionCount('approval')}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports & Analytics
            {getTabPermissionCount('reports') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                {getTabPermissionCount('reports')}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="management" className="gap-2">
            <User className="h-4 w-4" />
            Management
            {getTabPermissionCount('management') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                {getTabPermissionCount('management')}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="data" className="gap-2">
            <FileText className="h-4 w-4" />
            Data & Integration
            {getTabPermissionCount('data') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                {getTabPermissionCount('data')}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="administration" className="gap-2">
            <Settings className="h-4 w-4" />
            Administration
            {getTabPermissionCount('administration') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
                {getTabPermissionCount('administration')}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Users
            <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">
              {users.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Role Details Tab */}
        <TabsContent value="details" className="space-y-6">

          {/* Role Details Section Header */}
          <div className="flex items-start gap-3 pb-4 border-b">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Role Details</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Configure the basic information for this role
              </p>
            </div>
          </div>

          {/* Role Name & Description - Combined */}
          <div className="p-4 rounded-lg border bg-muted/50 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="role-name" className="text-sm font-medium">
                Role Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="role-name"
                value={roleName}
                onChange={(e) => {
                  const newName = e.target.value;
                  setRoleName(newName);
                  setHasUnsavedChanges(true);
                  // Real-time validation
                  if (!newName.trim()) {
                    setNameError('Role name is required');
                  } else if (newName.trim().length < 2) {
                    setNameError('Role name must be at least 2 characters');
                  } else {
                    setNameError('');
                  }
                }}
                placeholder="e.g., Project Manager"
                disabled={isSystem}
                className={`max-w-md ${nameError ? 'border-destructive' : ''}`}
              />
              {nameError && (
                <p className="text-xs text-destructive">{nameError}</p>
              )}
              {!nameError && (
                <p className="text-xs text-muted-foreground">
                  {isSystem 
                    ? 'Platform role names cannot be changed'
                    : 'The display name for this role'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role-description" className="text-sm font-medium">
                Description
              </Label>
              <Textarea
                id="role-description"
                value={roleDescription}
                onChange={(e) => {
                  setRoleDescription(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                placeholder="Describe what this role can do..."
                rows={3}
                className="max-w-2xl"
              />
              <p className="text-xs text-muted-foreground">
                A brief description of what this role can do
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="role-active" className="text-sm font-medium">
                    Active
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Enable this role to make it available for assignment to users
                  </p>
                </div>
                <div className="relative group">
                  <Switch
                    id="role-active"
                    checked={isActive}
                    onCheckedChange={(checked) => {
                      setIsActive(checked);
                      setHasUnsavedChanges(true);
                    }}
                    disabled={isSystem}
                  />
                  {isSystem && (
                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block w-64 p-2 bg-popover text-popover-foreground text-sm rounded-md border shadow-md z-10">
                      Platform roles must remain active as they are required for system functionality
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section Header */}
          <div className="flex items-start gap-3 pb-4 border-b pt-6">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Summary</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Overview of enabled permissions across all categories
              </p>
            </div>
          </div>

          {/* Summary Content */}
          <div className="grid grid-cols-3 gap-6 max-w-3xl">
            <button
              onClick={() => setActiveTab('users')}
              className="rounded-lg border p-4 text-left hover:bg-accent hover:border-primary transition-all cursor-pointer group"
            >
              <div className="flex items-center justify-between">
                <p className="text-3xl font-bold">{users.length}</p>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Users assigned to this role
              </p>
              <p className="text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                Click to view users →
              </p>
            </button>
            <div className="rounded-lg border p-4">
              <p className="text-3xl font-bold">{getTotalPermissionsCount()}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Total permissions enabled
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded-full ${isActive ? 'bg-success-500' : 'bg-neutral-400'}`} />
                <p className="text-3xl font-bold">{isActive ? 'Active' : 'Inactive'}</p>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Role status
              </p>
            </div>
          </div>
          
          <div className="space-y-2 max-w-2xl">
            <p className="text-sm font-medium">Permissions by Category:</p>
            {permissionCategories.map((category) => {
              const summaryCount = getCategorySummaryCount(category.id);
              const percentage = summaryCount.total > 0 ? Math.round((summaryCount.enabled / summaryCount.total) * 100) : 0;
              
              return (
                <div key={category.id} className="flex items-center justify-between text-sm py-2">
                  <div className="flex items-center gap-2">
                    {getIcon(category.iconName, 'h-4 w-4')}
                    <span>{category.name}</span>
                  </div>
                  <span className="text-muted-foreground">
                    {summaryCount.enabled}/{summaryCount.total} ({percentage}%)
                  </span>
                </div>
              );
            })}
          </div>

        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          {/* Users Section Header */}
          <div className="flex items-start gap-3 pb-4 border-b">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Users Assigned to {roleName}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Manage users assigned to this role and transfer them to different roles
              </p>
            </div>
          </div>

          {/* Users List */}
          {users.length === 0 ? (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-muted/20">
              <div className="text-center space-y-2">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No Users Assigned</p>
                <p className="text-sm text-muted-foreground">There are currently no users assigned to this role</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {users.length} {users.length === 1 ? 'user' : 'users'} assigned to this role
                </p>
              </div>

              <div className="border rounded-lg divide-y">
                {users.map((user) => (
                  <div key={user.id} className="p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                          {user.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTransferRole(user)}
                        >
                          Transfer Role
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Time Tracking Permissions Tab */}
        <TabsContent value="time-tracking" className="space-y-6">
          {/* Bulk Actions Dropdown */}
          <div className="flex items-center justify-end pb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2">
                  <Settings2 className="h-4 w-4" />
                  Bulk Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleSelectAllTimeTracking}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Select All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeselectAllTimeTracking}>
                  <Square className="mr-2 h-4 w-4" />
                  Deselect All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Personal Homepage Section */}
          <div className="flex items-start gap-3 pb-4 border-b">
            <div className="rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400 p-2.5">
              <LayoutDashboard className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Personal Homepage</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Access to personal homepage and metrics
              </p>
            </div>
          </div>

          {/* Personal Homepage Permissions */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="dashboard.view_own" className="text-sm font-medium cursor-pointer">
                  View Personal Homepage
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Access to personal homepage with dashboard and metrics
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Checkbox
                  id="dashboard.view_own"
                  checked={enabledPermissions.has('dashboard.view_own')}
                  onCheckedChange={(checked) => {
                    const newPermissions = new Set(enabledPermissions);
                    if (checked) {
                      newPermissions.add('dashboard.view_own');
                    } else {
                      newPermissions.delete('dashboard.view_own');
                    }
                    setEnabledPermissions(newPermissions);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Timesheet Access Section */}
          <div className="flex items-start gap-3 pb-4 border-b pt-6">
            <div className="rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400 p-2.5">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Timesheet Access</h3>
              <p className="text-sm text-muted-foreground mt-1">
                View, edit, and delete timesheet permissions
              </p>
            </div>
          </div>

          {/* Timesheet Access Settings */}
          <div className="border rounded-lg divide-y">
            {/* View timesheets */}
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="view-timesheets" className="text-sm font-medium">
                  View timesheets
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Access level for viewing timesheet entries
                </p>
              </div>
              <div className="w-48">
                <Select
                  value={viewTimesheetsAccess}
                  onValueChange={(value) => {
                    setViewTimesheetsAccess(value);
                    setHasUnsavedChanges(true);
                  }}
                >
                  <SelectTrigger id="view-timesheets">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="own">Own only</SelectItem>
                    <SelectItem value="team">Team (direct reports)</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Edit timesheets */}
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="edit-timesheets" className="text-sm font-medium">
                  Edit timesheets
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Access level for modifying timesheet entries
                </p>
              </div>
              <div className="w-48">
                <Select
                  value={editTimesheetsAccess}
                  onValueChange={(value) => {
                    setEditTimesheetsAccess(value);
                    setHasUnsavedChanges(true);
                  }}
                >
                  <SelectTrigger id="edit-timesheets">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="own">Own only</SelectItem>
                    <SelectItem value="team">Team (direct reports)</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Allow Delete timesheets */}
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="allow-delete-timesheets" className="text-sm font-medium cursor-pointer">
                  Allow Delete timesheets
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Allow deleting of timesheet lines
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Checkbox
                  id="allow-delete-timesheets"
                  checked={allowDeleteTimesheets}
                  onCheckedChange={(checked) => {
                    setAllowDeleteTimesheets(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
            </div>

            {/* Allow Duplicate timesheets lines */}
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="allow-duplicate-timesheets" className="text-sm font-medium cursor-pointer">
                  Allow Duplicate timesheets lines
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Allow duplicate of timesheet lines
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Checkbox
                  id="allow-duplicate-timesheets"
                  checked={allowDuplicateTimesheets}
                  onCheckedChange={(checked) => {
                    setAllowDuplicateTimesheets(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
            </div>

            {/* Recall Timesheet */}
            <div className="p-4 flex items-start justify-between gap-4 border-t">
              <div className="flex-1">
                <Label htmlFor="allow-recall-timesheet" className="text-sm font-medium cursor-pointer">
                  Recall Timesheet
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Allow recalling submitted timesheets for corrections
                </p>
              </div>
              <div className="flex items-center pt-0.5">
                <Checkbox
                  id="allow-recall-timesheet"
                  checked={allowRecallTimesheet}
                  onCheckedChange={(checked) => {
                    setAllowRecallTimesheet(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Smart Features Permissions Section */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold">Smart Features Permissions</h3>
                <p className="text-sm text-muted-foreground">Control access to intelligent time entry features</p>
              </div>
            </div>

            <TooltipProvider>
              <div className="border rounded-lg divide-y">
                {/* Enable 'Copy from Last Week' */}
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label 
                      htmlFor="enable-copy-from-last-week" 
                      className={!globalSettings.enableCopyPreviousWeek ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                    >
                      Enable 'Copy from Last Week'
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Allow users to copy entries from previous week
                    </p>
                  </div>
                  <div className="flex items-center pt-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Checkbox
                            id="enable-copy-from-last-week"
                            checked={enableCopyFromLastWeek}
                            disabled={!globalSettings.enableCopyPreviousWeek}
                            onCheckedChange={(checked) => {
                              setEnableCopyFromLastWeek(checked as boolean);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      {!globalSettings.enableCopyPreviousWeek && (
                        <TooltipContent>
                          <p>This feature must be enabled in General Settings first</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>

                {/* Copy Description */}
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label 
                      htmlFor="enable-copy-description" 
                      className={!globalSettings.copyDescriptions ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                    >
                      Copy Description
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Include entry descriptions when copying
                    </p>
                  </div>
                  <div className="flex items-center pt-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Checkbox
                            id="enable-copy-description"
                            checked={enableCopyDescription}
                            disabled={!globalSettings.copyDescriptions}
                            onCheckedChange={(checked) => {
                              setEnableCopyDescription(checked as boolean);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      {!globalSettings.copyDescriptions && (
                        <TooltipContent>
                          <p>This feature must be enabled in General Settings first</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>

                {/* Copy only Billable Entries */}
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label 
                      htmlFor="enable-copy-only-billable" 
                      className={!globalSettings.copyOnlyBillable ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                    >
                      Copy only Billable Entries
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      When copying, exclude non-billable time
                    </p>
                  </div>
                  <div className="flex items-center pt-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Checkbox
                            id="enable-copy-only-billable"
                            checked={enableCopyOnlyBillable}
                            disabled={!globalSettings.copyOnlyBillable}
                            onCheckedChange={(checked) => {
                              setEnableCopyOnlyBillable(checked as boolean);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      {!globalSettings.copyOnlyBillable && (
                        <TooltipContent>
                          <p>This feature must be enabled in General Settings first</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>

                {/* Enable Auto Smart Fill */}
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label 
                      htmlFor="enable-auto-smart-fill" 
                      className={!globalSettings.enableAutoSmartFill ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                    >
                      Enable Auto Smart Fill
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Automatically suggest time entries based on patterns
                    </p>
                  </div>
                  <div className="flex items-center pt-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Checkbox
                            id="enable-auto-smart-fill"
                            checked={enableAutoSmartFill}
                            disabled={!globalSettings.enableAutoSmartFill}
                            onCheckedChange={(checked) => {
                              setEnableAutoSmartFill(checked as boolean);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      {!globalSettings.enableAutoSmartFill && (
                        <TooltipContent>
                          <p>This feature must be enabled in General Settings first</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>

                {/* Enable AI Insights */}
                <div className="p-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label 
                      htmlFor="enable-ai-insights" 
                      className={!globalSettings.enableAiInsights ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                    >
                      Enable AI Insights
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Show AI-powered insights and anomaly detection
                    </p>
                  </div>
                  <div className="flex items-center pt-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Checkbox
                            id="enable-ai-insights"
                            checked={enableAiInsights}
                            disabled={!globalSettings.enableAiInsights}
                            onCheckedChange={(checked) => {
                              setEnableAiInsights(checked as boolean);
                              setHasUnsavedChanges(true);
                            }}
                          />
                        </div>
                      </TooltipTrigger>
                      {!globalSettings.enableAiInsights && (
                        <TooltipContent>
                          <p>This feature must be enabled in General Settings first</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>
              </div>
            </TooltipProvider>
          </div>
        </TabsContent>

        {/* Approval Permissions Tab */}
        <TabsContent value="approval" className="space-y-6">
          {/* Bulk Actions Dropdown */}
          <div className="flex items-center justify-end pb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2">
                  <Settings2 className="h-4 w-4" />
                  Bulk Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleSelectAllApproval}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Select All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeselectAllApproval}>
                  <Square className="mr-2 h-4 w-4" />
                  Deselect All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Approval Homepage Section */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold">Approval Homepage</h3>
                <p className="text-sm text-muted-foreground">Access to approval homepage and metrics</p>
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="approval.view_dashboard" className="text-sm font-medium cursor-pointer">
                    View Homepage
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View approval homepage with metrics and action items
                  </p>
                </div>
                <Checkbox
                  id="approval.view_dashboard"
                  checked={viewApprovalDashboard}
                  onCheckedChange={(checked) => {
                    setViewApprovalDashboard(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>
            </div>
          </div>

          {/* Timesheet Approval Section */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <ClipboardCheck className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold">Timesheet Approval</h3>
                <p className="text-sm text-muted-foreground">View and manage timesheet approvals</p>
              </div>
            </div>

            <div className="border rounded-lg divide-y">
              {/* View Approvals */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="view-approvals" className="text-sm font-medium cursor-pointer">
                    View Approvals
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View timesheet approvals
                  </p>
                </div>
                <Checkbox
                  id="view-approvals"
                  checked={viewApprovals}
                  onCheckedChange={(checked) => {
                    setViewApprovals(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Edit Timesheets */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="edit-timesheets-approval" className="text-sm font-medium cursor-pointer">
                    Edit Timesheets
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Modify time entries while reviewing for approval
                  </p>
                </div>
                <Checkbox
                  id="edit-timesheets-approval"
                  checked={editTimesheetsApproval}
                  onCheckedChange={(checked) => {
                    setEditTimesheetsApproval(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Reject Timesheets */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="reject-timesheets" className="text-sm font-medium cursor-pointer">
                    Reject Timesheets
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Reject timesheets
                  </p>
                </div>
                <Checkbox
                  id="reject-timesheets"
                  checked={rejectTimesheets}
                  onCheckedChange={(checked) => {
                    setRejectTimesheets(checked as boolean);
                    setHasUnsavedChanges(true);
                    // If reject is disabled, also disable compulsory rejection reason
                    if (!checked) {
                      setCompulsoryRejectionReason(false);
                    }
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Compulsory Rejection Reason - Only shown when Reject Timesheets is enabled */}
              {rejectTimesheets && (
                <div className="p-4 flex items-start justify-between gap-4 bg-muted/30">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="compulsory-rejection-reason" className="text-sm font-medium cursor-pointer">
                      Compulsory Rejection Reason
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Force reason for rejection
                    </p>
                  </div>
                  <Checkbox
                    id="compulsory-rejection-reason"
                    checked={compulsoryRejectionReason}
                    onCheckedChange={(checked) => {
                      setCompulsoryRejectionReason(checked as boolean);
                      setHasUnsavedChanges(true);
                    }}
                    className="mt-0.5"
                  />
                </div>
              )}

              {/* Bulk Approve */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="bulk-approve" className="text-sm font-medium cursor-pointer">
                    Bulk Approve
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Approve multiple timesheets at once
                  </p>
                </div>
                <Checkbox
                  id="bulk-approve"
                  checked={bulkApprove}
                  onCheckedChange={(checked) => {
                    setBulkApprove(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Delegate Approval */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="delegate-approval" className="text-sm font-medium cursor-pointer">
                    Delegate Approval
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Allow this user to delegate their approval responsibilities to another user
                  </p>
                </div>
                <Checkbox
                  id="delegate-approval"
                  checked={delegateApproval}
                  onCheckedChange={(checked) => {
                    setDelegateApproval(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* View Policy Violations */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="view-policy-violations" className="text-sm font-medium cursor-pointer">
                    View Policy Violations
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View policy violations
                  </p>
                </div>
                <Checkbox
                  id="view-policy-violations"
                  checked={viewPolicyViolations}
                  onCheckedChange={(checked) => {
                    setViewPolicyViolations(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* View Detected Issues */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="view-detected-issues" className="text-sm font-medium cursor-pointer">
                    View Detected Issues
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View detected issues
                  </p>
                </div>
                <Checkbox
                  id="view-detected-issues"
                  checked={viewDetectedIssues}
                  onCheckedChange={(checked) => {
                    setViewDetectedIssues(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Reports & Analytics Permissions Tab */}
        <TabsContent value="reports" className="space-y-6">
          {/* Bulk Actions Dropdown */}
          <div className="flex items-center justify-end pb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2">
                  <Settings2 className="h-4 w-4" />
                  Bulk Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleSelectAllReports}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Select All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeselectAllReports}>
                  <Square className="mr-2 h-4 w-4" />
                  Deselect All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Report Access Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <FileBarChart className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Report Access</h3>
                <p className="text-sm text-muted-foreground">Control access to various reports and analytics</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Timesheet Report */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="enable-timesheet-report" className="text-base font-medium cursor-pointer">
                      Timesheet Report
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Access to detailed timesheet reports showing time entries, projects, and tasks
                    </p>
                  </div>
                  <Checkbox
                    id="enable-timesheet-report"
                    checked={enableTimesheetReport}
                    onCheckedChange={(checked) => {
                      setEnableTimesheetReport(checked as boolean);
                      setHasUnsavedChanges(true);
                      if (!checked) {
                        setAllowTimesheetReportExport(false);
                      }
                    }}
                    className="mt-0.5"
                  />
                </div>

                {/* Conditional Options */}
                {enableTimesheetReport && (
                  <div className="border-t pt-4 space-y-4 bg-muted/30 -mx-4 px-4 -mb-4 pb-4 rounded-b-lg">
                    {/* View Access Level */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="view-timesheet-report-access" className="text-sm font-medium">
                          View Timesheet Report
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Define which timesheet data users can view in reports
                        </p>
                      </div>
                      <Select
                        value={viewTimesheetReportAccess}
                        onValueChange={(value) => {
                          setViewTimesheetReportAccess(value);
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="view-timesheet-report-access" className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="own">Own Only</SelectItem>
                          <SelectItem value="team">Team (direct reports)</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Allow Export */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="allow-timesheet-export" className="text-sm font-medium cursor-pointer">
                          Allow Export
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Allow exporting timesheet report data to Excel or CSV
                        </p>
                      </div>
                      <Checkbox
                        id="allow-timesheet-export"
                        checked={allowTimesheetReportExport}
                        onCheckedChange={(checked) => {
                          setAllowTimesheetReportExport(checked as boolean);
                          setHasUnsavedChanges(true);
                        }}
                        className="mt-0.5"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Missing Timesheet Report */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="enable-missing-timesheet-report" className="text-base font-medium cursor-pointer">
                      Missing Timesheet Report
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Access to reports showing employees with missing or incomplete timesheets
                    </p>
                  </div>
                  <Checkbox
                    id="enable-missing-timesheet-report"
                    checked={enableMissingTimesheetReport}
                    onCheckedChange={(checked) => {
                      setEnableMissingTimesheetReport(checked as boolean);
                      setHasUnsavedChanges(true);
                      if (!checked) {
                        setAllowMissingTimesheetReportExport(false);
                      }
                    }}
                    className="mt-0.5"
                  />
                </div>

                {/* Conditional Options */}
                {enableMissingTimesheetReport && (
                  <div className="border-t pt-4 space-y-4 bg-muted/30 -mx-4 px-4 -mb-4 pb-4 rounded-b-lg">
                    {/* View Access Level */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="view-missing-timesheet-report-access" className="text-sm font-medium">
                          View Missing Timesheet Report
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Define which missing timesheet data users can view in reports
                        </p>
                      </div>
                      <Select
                        value={viewMissingTimesheetReportAccess}
                        onValueChange={(value) => {
                          setViewMissingTimesheetReportAccess(value);
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="view-missing-timesheet-report-access" className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="own">Own Only</SelectItem>
                          <SelectItem value="team">Team (direct reports)</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Allow Export */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="allow-missing-timesheet-export" className="text-sm font-medium cursor-pointer">
                          Allow Export
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Allow exporting missing timesheet report data to Excel or CSV
                        </p>
                      </div>
                      <Checkbox
                        id="allow-missing-timesheet-export"
                        checked={allowMissingTimesheetReportExport}
                        onCheckedChange={(checked) => {
                          setAllowMissingTimesheetReportExport(checked as boolean);
                          setHasUnsavedChanges(true);
                        }}
                        className="mt-0.5"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Utilisation Report */}
              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="enable-utilisation-report" className="text-base font-medium cursor-pointer">
                      Utilisation Report
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Access to utilisation reports showing resource allocation and capacity metrics
                    </p>
                  </div>
                  <Checkbox
                    id="enable-utilisation-report"
                    checked={enableUtilisationReport}
                    onCheckedChange={(checked) => {
                      setEnableUtilisationReport(checked as boolean);
                      setHasUnsavedChanges(true);
                      if (!checked) {
                        setAllowUtilisationReportExport(false);
                      }
                    }}
                    className="mt-0.5"
                  />
                </div>

                {/* Conditional Options */}
                {enableUtilisationReport && (
                  <div className="border-t pt-4 space-y-4 bg-muted/30 -mx-4 px-4 -mb-4 pb-4 rounded-b-lg">
                    {/* View Access Level */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="view-utilisation-report-access" className="text-sm font-medium">
                          View Utilisation Report
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Define which utilisation data users can view in reports
                        </p>
                      </div>
                      <Select
                        value={viewUtilisationReportAccess}
                        onValueChange={(value) => {
                          setViewUtilisationReportAccess(value);
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger id="view-utilisation-report-access" className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="own">Own Only</SelectItem>
                          <SelectItem value="team">Team (direct reports)</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Allow Export */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="allow-utilisation-export" className="text-sm font-medium cursor-pointer">
                          Allow Export
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Allow exporting utilisation report data to Excel or CSV
                        </p>
                      </div>
                      <Checkbox
                        id="allow-utilisation-export"
                        checked={allowUtilisationReportExport}
                        onCheckedChange={(checked) => {
                          setAllowUtilisationReportExport(checked as boolean);
                          setHasUnsavedChanges(true);
                        }}
                        className="mt-0.5"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Billable Hours Report removed — belongs in the Projects module of the PSA suite. */}
            </div>
          </div>

        </TabsContent>

        {/* Management Permissions Tab */}
        <TabsContent value="management" className="space-y-6">
          {/* Bulk Actions Dropdown */}
          <div className="flex items-center justify-end pb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2">
                  <Settings2 className="h-4 w-4" />
                  Bulk Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleSelectAllManagement}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Select All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeselectAllManagement}>
                  <Square className="mr-2 h-4 w-4" />
                  Deselect All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Employee Directory Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Employee Directory</h3>
                <p className="text-sm text-muted-foreground">Manage access to employee information and profiles</p>
              </div>
            </div>

            <div className="border rounded-lg divide-y">
              {/* View Employee Directory */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="view-employee-directory" className="text-sm font-medium cursor-pointer">
                    View Employee Directory
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View the employee directory and basic information
                  </p>
                </div>
                <Checkbox
                  id="view-employee-directory"
                  checked={viewEmployeeDirectory}
                  onCheckedChange={(checked) => {
                    setViewEmployeeDirectory(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* View Employee Details */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label htmlFor="view-employee-details" className="text-sm font-medium cursor-pointer">
                    View Employee Details
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View detailed employee information and profiles
                  </p>
                </div>
                <Checkbox
                  id="view-employee-details"
                  checked={viewEmployeeDetails}
                  onCheckedChange={(checked) => {
                    setViewEmployeeDetails(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Data & Integration Permissions Tab */}
        <TabsContent value="data" className="space-y-6">
          {/* Bulk Actions Dropdown */}
          <div className="flex items-center justify-end pb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2">
                  <Settings2 className="h-4 w-4" />
                  Bulk Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleSelectAllDataIntegration}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Select All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeselectAllDataIntegration}>
                  <Square className="mr-2 h-4 w-4" />
                  Deselect All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Integrations Section */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <Plug className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold">Integrations</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage access to system integrations and data synchronisation
                </p>
              </div>
            </div>

            <div className="border rounded-lg divide-y">
              {/* View Integrations */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label 
                    htmlFor="view-integrations" 
                    className="text-sm font-medium cursor-pointer"
                  >
                    View Integrations
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View integration configurations and status
                  </p>
                </div>
                <Checkbox
                  id="view-integrations"
                  checked={viewIntegrations}
                  onCheckedChange={(checked) => {
                    setViewIntegrations(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Integrations */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label 
                    htmlFor="manage-integrations" 
                    className="text-sm font-medium cursor-pointer"
                  >
                    Manage Integrations
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Configure, connect, and disconnect system integrations
                  </p>
                </div>
                <Checkbox
                  id="manage-integrations"
                  checked={manageIntegrations}
                  onCheckedChange={(checked) => {
                    setManageIntegrations(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>
            </div>
          </div>

          {/* Company Management Section */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold">Company Management</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage access to company information and settings
                </p>
              </div>
            </div>

            <div className="border rounded-lg divide-y">
              {/* View Companies */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label 
                    htmlFor="view-companies" 
                    className="text-sm font-medium cursor-pointer"
                  >
                    View Companies
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    View company list and basic information
                  </p>
                </div>
                <Checkbox
                  id="view-companies"
                  checked={viewCompanies}
                  onCheckedChange={(checked) => {
                    setViewCompanies(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Companies */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <Label 
                    htmlFor="manage-companies" 
                    className="text-sm font-medium cursor-pointer"
                  >
                    Manage Companies
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Create, edit, and manage company settings
                  </p>
                </div>
                <Checkbox
                  id="manage-companies"
                  checked={manageCompanies}
                  onCheckedChange={(checked) => {
                    setManageCompanies(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Administration Permissions Tab */}
        <TabsContent value="administration" className="space-y-6">
          {/* Bulk Actions Dropdown */}
          <div className="flex items-center justify-end pb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2">
                  <Settings2 className="h-4 w-4" />
                  Bulk Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleSelectAllAdministration}>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Select All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeselectAllAdministration}>
                  <Square className="mr-2 h-4 w-4" />
                  Deselect All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* System Settings Section */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
                <Settings className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold">System Settings</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Control access to system-wide configuration and administrative functions
                </p>
              </div>
            </div>

            <div className="border rounded-lg divide-y">
              {/* Manage General Settings - Primary Control */}
              <div className="p-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="manage-general-settings" className="text-sm font-medium">
                      Manage General Settings
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      View or modify core system configuration including company details, branding, and default preferences
                    </p>
                  </div>
                  <Select
                    value={manageGeneralSettings}
                    onValueChange={(value: 'view' | 'configure') => {
                      setManageGeneralSettings(value);
                      // If switching to "view", disable all dependent permissions
                      if (value === 'view') {
                        setManageDeadlines(false);
                        setManagePolicySettings(false);
                        setManageUtilisationSettings(false);
                        setManageTimeEntrySettings(false);
                        setManageWorkTypeSettings(false);
                        setManageApprovalsSettings(false);
                        setManageNotifications(false);
                        // System roles must always retain manageRolesPermissions
                        if (!isSystem) {
                          setManageRolesPermissions(false);
                        }
                        setManageScreenDataModes(false);
                      }
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">View Only</SelectItem>
                      <SelectItem value="configure">Configure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Manage Deadlines */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label 
                            htmlFor="manage-deadlines" 
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Deadlines
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Define and adjust timesheet submission cutoff dates, approval deadlines, and period-end schedules
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-deadlines"
                  checked={manageDeadlines}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManageDeadlines(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Policy Settings */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label 
                            htmlFor="manage-policy-settings" 
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Policy Settings
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Configure automated rules for detecting and flagging policy violations, including overtime limits and break requirements
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-policy-settings"
                  checked={managePolicySettings}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManagePolicySettings(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Utilisation Settings */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label 
                            htmlFor="manage-utilisation-settings" 
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Utilisation Settings
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Set target utilization rates, billability thresholds, and capacity planning parameters for resources
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-utilisation-settings"
                  checked={manageUtilisationSettings}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManageUtilisationSettings(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Time Entry Rules */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label
                            htmlFor="manage-time-entry-settings"
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Time Entry Rules
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Configure time entry validation rules, rounding settings, historical entry limits, and smart fill behaviour
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-time-entry-settings"
                  checked={manageTimeEntrySettings}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManageTimeEntrySettings(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Work Type */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label
                            htmlFor="manage-work-type-settings"
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Work Type
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Define and manage overtime types, work classifications, and pay categories used across timesheets
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-work-type-settings"
                  checked={manageWorkTypeSettings}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManageWorkTypeSettings(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Approvals */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label 
                            htmlFor="manage-approvals-settings" 
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Approvals
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Design and modify approval chains, set approval hierarchies, and configure escalation rules for timesheets and requests
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-approvals-settings"
                  checked={manageApprovalsSettings}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManageApprovalsSettings(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Notifications */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label 
                            htmlFor="manage-notifications" 
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Notifications
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Control when and how users receive email, in-app, and push notifications for various system events and reminders
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-notifications"
                  checked={manageNotifications}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManageNotifications(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Roles & Permissions */}
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label 
                            htmlFor="manage-roles-permissions" 
                            className={(manageGeneralSettings === 'view' || isSystem) ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Roles & Permissions
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Create custom user roles, assign permission levels, and control what different user types can access and modify
                          </p>
                        </div>
                      </TooltipTrigger>
                      {isSystem && (
                        <TooltipContent>
                          <p>This permission cannot be disabled on the Administrator role</p>
                        </TooltipContent>
                      )}
                      {!isSystem && manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-roles-permissions"
                  checked={isSystem ? true : manageRolesPermissions}
                  disabled={manageGeneralSettings === 'view' || isSystem}
                  onCheckedChange={(checked) => {
                    setManageRolesPermissions(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>

              {/* Manage Screen Data Modes — HIDDEN from UI pending Settings menu visibility decision.
                  Re-enable by changing {false && ...} back to the full block below.
                  See: docs/features/roles-and-permissions.md → "Hidden: Manage Screen Data Modes" */}
              {false && (
              <div className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Label
                            htmlFor="manage-screen-data-modes"
                            className={manageGeneralSettings === 'view' ? 'text-sm font-medium text-muted-foreground cursor-not-allowed' : 'text-sm font-medium cursor-pointer'}
                          >
                            Manage Screen Data Modes
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Enable or disable demonstration mode features, sample data visibility, and training environment settings
                          </p>
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && (
                        <TooltipContent>
                          <p>Requires 'Configure' access to General Settings</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Checkbox
                  id="manage-screen-data-modes"
                  checked={manageScreenDataModes}
                  disabled={manageGeneralSettings === 'view'}
                  onCheckedChange={(checked) => {
                    setManageScreenDataModes(checked as boolean);
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-0.5"
                />
              </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
        </div>
      </div>

      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Role Changes</DialogTitle>
            <DialogDescription>
              Review the role configuration before saving
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Role Summary */}
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Role:</span>
                <span className="text-sm">{roleName}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Users affected:</span>
                <span className="text-sm">{users.length} {users.length === 1 ? 'user' : 'users'}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Total permissions enabled:</span>
                <span className="text-sm font-semibold">{getTotalPermissionsCount()}</span>
              </div>
            </div>

            <Separator />

            {/* Confirmation Message */}
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 p-3 border border-blue-200 dark:border-blue-800">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  These changes will be applied to all {users.length} {users.length === 1 ? 'user' : 'users'} with this role.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSaveConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={performSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Confirm & Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Role Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer User to Different Role</DialogTitle>
            <DialogDescription>
              Select a new role for {selectedUser?.name}. This will immediately update their permissions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Role</Label>
              <div className="p-3 rounded-lg border bg-muted/50">
                <p className="font-medium">{roleName}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-role">New Role</Label>
              <Select value={newRoleId} onValueChange={setNewRoleId}>
                <SelectTrigger id="new-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {allRoles
                    .filter(role => String(role.id) !== roleId)
                    .map(role => (
                      <SelectItem key={role.id} value={String(role.id)}>
                        <div className="flex items-center gap-2">
                          <span>{role.name}</span>
                          {role.isSystem && (
                            <Badge variant="secondary" className="text-xs">Platform</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {selectedUser && (
              <div className="p-3 rounded-lg border bg-info-50 dark:bg-info-950/20">
                <p className="text-sm text-muted-foreground">
                  <strong>{selectedUser.name}</strong> will be transferred from <strong>{roleName}</strong> to the selected role.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTransferDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmTransfer}
              disabled={!newRoleId}
            >
              Transfer User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticky Footer with Save and Cancel Buttons */}
      <div className="sticky bottom-0 z-10 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 mt-8 -mx-2 sm:-mx-3 lg:-mx-3 xl:-mx-4 px-2 sm:px-3 lg:px-3 xl:px-4">
        <div className="flex items-center justify-end gap-3 py-4 max-w-[2000px] mx-auto">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isCreateMode ? false : !hasUnsavedChanges}
          >
            {isCreateMode ? 'Create Role' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};
